import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

export interface SeoMeta {
  /** Page title (also used as og:title / twitter:title). */
  title: string;
  /** Meta description (also used as og:description / twitter:description). */
  description: string;
  /** Absolute (or root-relative) image URL for OG/Twitter cards. */
  image?: string;
  /** Alt text for the OG/Twitter image. Defaults to the site name. */
  imageAlt?: string;
  /** Canonical URL for this page. Defaults to the prod origin + current path. */
  url?: string;
  /** Open Graph type (e.g. 'website', 'article'). Defaults to 'website'. */
  type?: string;
  /** robots directive (e.g. 'noindex, nofollow'). Defaults to 'index, follow'. */
  robots?: string;
}

/**
 * SeoService — per-page SEO + social metadata.
 *
 * Wraps Angular's {@link Title}/{@link Meta} plus DOCUMENT to manage:
 *  - <title> + meta description
 *  - canonical link
 *  - Open Graph + Twitter card tags
 *  - JSON-LD structured data (add/remove by id)
 *  - <html lang>
 *
 * Pages call {@link setMeta} on init; it is idempotent (re-setting a tag updates
 * it rather than appending), so navigating between pages keeps a single clean
 * head. Static defaults live in index.html; this overrides them per route.
 */
@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly doc = inject<Document>(DOCUMENT);

  private static readonly SITE_NAME = 'Actively';
  /** Fixed production origin — canonical/og:url must be stable across envs. */
  private static readonly SITE_ORIGIN = 'https://actively.gr';
  /** Absolute 1200x630 raster share card. SVG og:images are not rendered by
      social platforms, so this is a JPEG. */
  private static readonly DEFAULT_IMAGE =
    'https://actively.gr/assets/og-card.jpg';

  /** Set the full set of SEO/social tags for the current page. */
  setMeta(config: SeoMeta): void {
    const url = config.url ? this.absolute(config.url) : this.canonicalUrl();
    const image = this.absolute(config.image ?? SeoService.DEFAULT_IMAGE);
    const imageAlt = config.imageAlt ?? SeoService.SITE_NAME;
    const type = config.type ?? 'website';
    const robots = config.robots ?? 'index, follow, max-image-preview:large';

    this.title.setTitle(config.title);

    // An empty description means "this page has none": remove the tags rather
    // than emitting empty ones (or leaking the previous page's description).
    if (config.description) {
      this.upsertName('description', config.description);
    } else {
      this.meta.removeTag("name='description'");
    }
    this.upsertName('robots', robots);

    // noindex + canonical is a mixed signal (Google treats them as conflicting
    // hints), so noindexed pages carry no canonical link at all.
    if (robots.includes('noindex')) {
      this.removeCanonical();
    } else {
      this.setCanonical(url);
    }

    // Open Graph
    this.upsertProperty('og:title', config.title);
    if (config.description) {
      this.upsertProperty('og:description', config.description);
    } else {
      this.meta.removeTag("property='og:description'");
    }
    this.upsertProperty('og:type', type);
    this.upsertProperty('og:url', url);
    this.upsertProperty('og:image', image);
    this.upsertProperty('og:image:alt', imageAlt);
    // The 1200x630/JPEG size hints describe the default card only; a custom
    // image (e.g. a club logo) has unknown dimensions, so drop the hints.
    if (image === SeoService.DEFAULT_IMAGE) {
      this.upsertProperty('og:image:width', '1200');
      this.upsertProperty('og:image:height', '630');
      this.upsertProperty('og:image:type', 'image/jpeg');
    } else {
      this.meta.removeTag("property='og:image:width'");
      this.meta.removeTag("property='og:image:height'");
      this.meta.removeTag("property='og:image:type'");
    }
    this.upsertProperty('og:site_name', SeoService.SITE_NAME);
    // Greek is the site default; English is offered via the in-app toggle.
    this.upsertProperty('og:locale', 'el_GR');
    this.upsertProperty('og:locale:alternate', 'en_US');

    // Twitter
    this.upsertName('twitter:card', 'summary_large_image');
    this.upsertName('twitter:title', config.title);
    if (config.description) {
      this.upsertName('twitter:description', config.description);
    } else {
      this.meta.removeTag("name='twitter:description'");
    }
    this.upsertName('twitter:image', image);
    this.upsertName('twitter:image:alt', imageAlt);
  }

  /**
   * Add or replace a JSON-LD <script> block, keyed by `id` so each page can own
   * its structured data and replace it on re-render.
   */
  setJsonLd(id: string, obj: Record<string, unknown>): void {
    const elementId = this.jsonLdId(id);
    let script = this.doc.getElementById(elementId) as HTMLScriptElement | null;
    if (!script) {
      script = this.doc.createElement('script');
      script.type = 'application/ld+json';
      script.id = elementId;
      this.doc.head.appendChild(script);
    }
    script.textContent = JSON.stringify(obj);
  }

  /** Remove a previously-added JSON-LD block by id. */
  removeJsonLd(id: string): void {
    const script = this.doc.getElementById(this.jsonLdId(id));
    script?.parentNode?.removeChild(script);
  }

  /** Set the document language (e.g. 'el', 'en'). */
  setLang(code: string): void {
    if (!code) {
      return;
    }
    this.doc.documentElement.setAttribute('lang', code);
  }

  // --- internals -----------------------------------------------------------

  private jsonLdId(id: string): string {
    return `jsonld-${id}`;
  }

  /** Canonical URL = fixed prod origin + current path (never the serving origin,
   *  so staging/preview/Capacitor builds don't self-canonicalize to the wrong host). */
  private canonicalUrl(): string {
    let path = '/';
    try {
      path = this.doc.location?.pathname || '/';
    } catch {
      path = '/';
    }
    return SeoService.SITE_ORIGIN + path;
  }

  /** Resolve a possibly-relative URL against the fixed prod origin. */
  private absolute(value: string): string {
    if (!value) return value;
    if (/^https?:\/\//i.test(value)) return value;
    return SeoService.SITE_ORIGIN + (value.startsWith('/') ? value : '/' + value);
  }

  private setCanonical(url: string): void {
    let link = this.doc.head.querySelector(
      "link[rel='canonical']"
    ) as HTMLLinkElement | null;
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private removeCanonical(): void {
    const link = this.doc.head.querySelector("link[rel='canonical']");
    link?.parentNode?.removeChild(link);
  }

  private upsertName(name: string, content: string): void {
    if (this.meta.getTag(`name='${name}'`)) {
      this.meta.updateTag({ name, content });
    } else {
      this.meta.addTag({ name, content });
    }
  }

  private upsertProperty(property: string, content: string): void {
    if (this.meta.getTag(`property='${property}'`)) {
      this.meta.updateTag({ property, content });
    } else {
      this.meta.addTag({ property, content });
    }
  }
}
